import { ReactNode } from 'react'

import clsx from 'clsx'

import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'

export const CheckBoxType = {
  CHECK: 'check',
  TOGGLE_TEXT: 'toggle-text',
  TOGGLE_BUTTON: 'toggle-button'
} as const

export type CheckBoxType = (typeof CheckBoxType)[keyof typeof CheckBoxType]

export const CheckBoxColor = {
  NORMAL: 'normal',
  EMPHASIS: 'emphasis',
  AI: 'ai'
} as const

export type CheckBoxColor = (typeof CheckBoxColor)[keyof typeof CheckBoxColor]

/** チェック時のAIグラデーション（text-ai / border-ai と同じ配色） */
const AI_CHECKED_CLASS =
  'peer-checked:bg-linear-to-r peer-checked:from-purple-800 peer-checked:via-[#cc00b7] peer-checked:to-red-700'

interface Props {
  type?: CheckBoxType
  label?: ReactNode
  name: string
  checked?: boolean
  disabled?: boolean
  incomplete?: boolean
  optionToggle?: boolean
  forDisclosure?: boolean
  forRow?: boolean
  py?: 4 | 8
  labelNode?: string | ReactNode
  nowrap?: boolean
  block?: boolean
  onChange(value: boolean): void
  color?: CheckBoxColor
  toggleText?: { left: string; right: string }
  baseline?: boolean
}

const CheckBox = ({
  type = CheckBoxType.CHECK,
  label,
  name,
  checked = false,
  disabled = false,
  incomplete = false,
  optionToggle = false,
  forDisclosure = false,
  forRow = false,
  py = 4,
  labelNode,
  nowrap = false,
  block = false,
  onChange,
  color = CheckBoxColor.NORMAL,
  toggleText,
  baseline = false
}: Props) => {
  const toggleTextClass =
    'text-size-13 shadow-button inline-block px-3 py-1.5 pointer-events-none outline-default'

  return type == CheckBoxType.TOGGLE_TEXT ? (
    <label className='inline-flex cursor-pointer whitespace-nowrap'>
      <input
        className='hidden-input peer'
        type='checkbox'
        name={name}
        checked={checked}
        onChange={() => onChange(!checked)}
        disabled={disabled}
      />
      <button
        className={clsx(
          'bg-input text-primary shadow-button peer-checked:shadow-inset-200 rounded-l-lg',
          toggleTextClass,
          optionToggle
            ? 'bg-fill-selected text-secondary peer-checked:bg-sumi-60'
            : 'peer-checked:bg-fill-success-secondary peer-checked:text-success'
        )}
        onClick={() => onChange(true)}
        disabled={checked}
      >
        {toggleText?.left ?? 'ON'}
      </button>
      <button
        className={clsx(
          `${
            optionToggle
              ? 'bg-fill-beni text-beni peer-checked:bg-input peer-checked:text-gray-900'
              : 'bg-fill-selected text-secondary peer-checked:bg-input peer-checked:text-primary'
          } shadow-inset-200 peer-checked:shadow-button -ml-px rounded-r-lg`,
          toggleTextClass
        )}
        onClick={() => onChange(false)}
        disabled={!checked}
      >
        {toggleText?.right ?? 'OFF'}
      </button>
    </label>
  ) : type === CheckBoxType.TOGGLE_BUTTON ? (
    <label>
      <input
        className='hidden-input peer'
        type='checkbox'
        name={name}
        checked={checked}
        onChange={() => onChange(!checked)}
      />
      <span
        className={clsx(
          'outline-default-peer bg-fill-selected shadow-inset-200 after:bg-fill-primary after:shadow-button peer-checked:bg-fill-success-secondary block h-[26px] w-[46px] rounded-full p-1 after:block after:h-[18px] after:min-h-[18px] after:w-[18px] after:min-w-[18px] after:rounded-full after:transition peer-checked:after:translate-x-full'
        )}
      ></span>
    </label>
  ) : (
    <label
      className={clsx(
        'text-size-13 inline-flex focus:outline-none',
        baseline ? 'items-baseline' : 'items-center',
        !disabled && (forDisclosure || forRow)
          ? '[&:has(input:checked)]:bg-surface-secondary-selected hover:[&:has(input:not(:checked))]:bg-surface-hover active:[&:has(input:not(:checked))]:bg-surface-active flex w-full rounded-lg'
          : block
            ? 'my-0 py-1'
            : 'w-fit py-1',
        forDisclosure && 'p-2',
        forRow && 'relative px-4 py-1',
        forDisclosure ? 'gap-2' : forRow ? 'gap-x-3' : 'gap-1',
        disabled && 'disabled-active-pointer-none',
        py == 4 ? 'py-1' : py == 8 ? 'py-2' : ''
      )}
    >
      <input
        className='hidden-input peer disabled:pointer-events-none'
        type='checkbox'
        name={name}
        checked={checked}
        onChange={() => onChange(!checked)}
        disabled={disabled}
      />
      <span
        className={clsx(
          'outline-default-peer border-border-input bg-input inline-flex h-4 min-h-4 w-4 min-w-4 items-center justify-center rounded border',
          // checked
          'peer-checked:border-none',
          color == CheckBoxColor.EMPHASIS
            ? 'peer-checked:bg-fill-emphasis'
            : color == CheckBoxColor.AI
              ? AI_CHECKED_CLASS
              : 'peer-checked:bg-fill-brand-selected',
          // disabled
          'peer-disabled:bg-fill-disabled peer-disabled:pointer-events-none peer-disabled:border-none',
          !disabled &&
            // hover & active
            'peer-[:not(:checked)]:peer-hover:border-border-input-hover peer-[:not(:checked)]:peer-hover:bg-input-hover peer-[:not(:checked)]:peer-active:border-border-input-active peer-[:not(:checked)]:peer-active:bg-input-active',
          (forDisclosure || forRow) && 'my-[5px]'
        )}
      >
        <Icon
          color={
            (disabled && checked) || (disabled && incomplete)
              ? IconColor.DISABLED
              : disabled
                ? IconColor.TRANSPARENT
                : checked
                  ? IconColor.ON_COLOR
                  : IconColor.TRANSPARENT
          }
          type={incomplete ? IconType.MINUS : IconType.CHECK}
        />
      </span>
      {label && (
        <span
          className={clsx(
            'leading-150 break-all',
            nowrap && 'whitespace-nowrap',
            (forDisclosure || forRow) && 'translate-y-[-3px] transform'
          )}
        >
          {label}
        </span>
      )}
      {labelNode && <>{labelNode}</>}
    </label>
  )
}

export default CheckBox
